"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import {
  Link2,
  Mail,
  Target,
  BarChart3,
  FileText,
  CheckCircle2,
  TrendingUp,
  Sparkles,
  Briefcase,
  ArrowRight,
  X,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { MarketingNav } from "@/components/MarketingNav";
import { MarketingFooter } from "@/components/MarketingFooter";
import { AuthCard } from "@/components/AuthCard";

export function LandingPage() {
  const [userCount, setUserCount] = useState<number | null>(null);

  useEffect(() => {
    fetch("/api/stats")
      .then((r) => r.json())
      .then((d) => setUserCount(d.userCount))
      .catch(() => setUserCount(0));
  }, []);

  const roleMatchData = [
    { role: "Product Manager", match: 87 },
    { role: "Technical PM", match: 81 },
    { role: "Operations Mgr", match: 52 },
  ];

  // Show founding members while loading (null) or when count <= 1000
  const showFoundingMembers = userCount === null || userCount <= 1000;

  return (
    <div className="min-h-screen bg-[#E6E4E4]">
      <MarketingNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1c3d51] via-[#2b4276] to-[#1F5867] px-4 py-16 sm:px-6 lg:px-8">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>
        <div className="relative mx-auto max-w-7xl">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <Badge className="mb-6 border border-[#C6A85E] bg-[#C6A85E]/10 text-[#C6A85E]">
                <Sparkles className="mr-1 h-3 w-3" />
                AI-Powered Job Tracking
              </Badge>
              <h1 className="mb-6 text-2xl font-bold leading-tight text-white md:text-3xl lg:text-4xl">
                The Job Application Tracker That Fills Itself
              </h1>
              <p className="mb-8 text-lg text-[#bedcee] md:text-xl">
                Stop managing your job search in spreadsheets. HireFit automatically extracts job
                details from links, scores them against your resume, and tracks everything in one
                powerful dashboard.
              </p>
              <Link
                href="/login"
                className="group inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#2a9695] to-[#1F5867] px-6 py-3 text-white hover:opacity-90"
              >
                Start Tracking for Free
                <ArrowRight className="h-5 w-5 text-[#C6A85E] transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            {/* Auth Card */}
            <div className="rounded-2xl border border-[#78d0d0]/30 bg-white p-8 shadow-2xl">
              <AuthCard />
            </div>
          </div>
        </div>
      </section>

      {/* Stop Managing in Spreadsheets Section */}
      <section className="bg-[#EFEFEF] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-2xl font-bold text-[#1c3d51] md:text-3xl">
              Stop Managing Your Job Search in Spreadsheets
            </h2>
            <p className="text-lg text-[#748491]">
              Let HireFit do the heavy lifting while you focus on preparing for interviews
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-2">
            <Card className="border-2 border-red-200 p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="rounded-full bg-red-100 p-2">
                  <X className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="font-semibold text-[#1c3d51]">Manual Spreadsheet</h3>
              </div>
              <ul className="space-y-3">
                {[
                  "Manually copy job descriptions",
                  "Track application dates yourself",
                  "No insights on resume fit",
                  "Difficult to track interview stages",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-[#748491]">
                    <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 rounded-lg bg-gray-50 p-4">
                <div className="mb-2 grid grid-cols-4 gap-2 border-b pb-2 text-xs font-medium text-gray-500">
                  <div>Company</div>
                  <div>Role</div>
                  <div>Date</div>
                  <div>Status</div>
                </div>
                <div className="text-xs text-gray-400">
                  <div className="grid grid-cols-4 gap-2">
                    <div>???</div>
                    <div>???</div>
                    <div>???</div>
                    <div>???</div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="border-2 border-[#2a9695] p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="rounded-full bg-[#c5f0f0] p-2">
                  <CheckCircle2 className="h-5 w-5 text-[#2a9695]" />
                </div>
                <h3 className="font-semibold text-[#1c3d51]">HireFit Dashboard</h3>
              </div>
              <ul className="space-y-3">
                {[
                  "Automatically capture job details",
                  "Track everything with one click",
                  "AI-powered resume match scores",
                  "Visual pipeline management",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-[#748491]">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#2a9695]" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 space-y-2 rounded-lg bg-gradient-to-br from-[#c5f0f0]/30 to-[#bedcee]/30 p-4">
                <div className="flex items-center justify-between rounded-md bg-white p-2">
                  <span className="text-xs font-medium text-[#1c3d51]">Senior Product Manager</span>
                  <Badge className="bg-[#2a9695] text-xs text-white">87%</Badge>
                </div>
                <div className="flex items-center justify-between rounded-md bg-white p-2">
                  <span className="text-xs font-medium text-[#1c3d51]">Technical PM</span>
                  <Badge className="bg-[#78d0d0] text-xs text-[#1c3d51]">81%</Badge>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Add Jobs Instantly Section */}
      <section className="bg-white px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-2xl font-bold text-[#1c3d51] md:text-3xl">Add Jobs Instantly</h2>
            <p className="text-lg text-[#748491]">
              Multiple ways to add jobs to your dashboard—fast and effortless
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            <Card className="bg-gradient-to-br from-white to-[#c5f0f0]/10 p-6 transition-shadow hover:shadow-lg">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#2a9695]">
                <Link2 className="h-6 w-6 text-white" />
              </div>
              <h3 className="mb-3 text-xl font-semibold text-[#1c3d51]">Paste Job Links</h3>
              <p className="text-sm text-[#748491]">
                Copy any job posting URL from LinkedIn, Indeed, or company career pages. HireFit
                automatically captures the title, company, location, salary, and full job description.
              </p>
            </Card>

            <Card className="bg-gradient-to-br from-white to-[#c5f0f0]/10 p-6 transition-shadow hover:shadow-lg">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#778ec3]">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <h3 className="mb-3 text-xl font-semibold text-[#1c3d51]">Forward Job Emails</h3>
              <p className="text-sm text-[#748491]">
                Forward job alert emails from any source to your unique HireFit email address. Jobs
                are automatically extracted and added to your dashboard—no manual entry required.
              </p>
            </Card>

            <Card className="bg-gradient-to-br from-white to-[#c5f0f0]/10 p-6 transition-shadow hover:shadow-lg">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#1F5867]">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <h3 className="mb-3 text-xl font-semibold text-[#1c3d51]">Auto-Discover Jobs</h3>
              <p className="text-sm text-[#748491]">
                Let HireFit find jobs for you. Based on your resume and preferences, the app discovers
                relevant positions and automatically adds high-match jobs to your dashboard.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Application Pipeline Section */}
      <section className="bg-[#EFEFEF] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-2xl font-bold text-[#1c3d51] md:text-3xl">
              All Your Applications in One Powerful Dashboard
            </h2>
            <p className="text-lg text-[#748491]">
              View job title, company, location, salary, match scores, status, and notes in a sortable
              table
            </p>
          </div>

          <Card className="p-6">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Link
                href="/login"
                className="inline-flex items-center gap-2 rounded-lg bg-[#2a9695] px-3 py-1.5 text-sm text-white hover:bg-[#1F5867]"
              >
                <Link2 className="h-4 w-4" />
                Paste Job
              </Link>
              <Link
                href="/login"
                className="inline-flex items-center gap-2 rounded-lg border border-[#bedcee] px-3 py-1.5 text-sm text-[#1c3d51] hover:bg-[#c5f0f0]/20"
              >
                <FileText className="h-4 w-4" />
                Upload Resume
              </Link>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-sm text-[#748491]">Filter by Score:</span>
                <select className="rounded-lg border border-[#bedcee] px-3 py-1 text-sm text-[#1c3d51] focus:border-[#2a9695] focus:outline-none">
                  <option>All Scores</option>
                  <option>80%+</option>
                  <option>60-79%</option>
                  <option>Below 60%</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#bedcee] text-left text-xs font-medium text-[#748491]">
                    <th className="pb-3 pr-4">Title</th>
                    <th className="pb-3 pr-4">Company</th>
                    <th className="pb-3 pr-4">Location</th>
                    <th className="pb-3 pr-4">Salary</th>
                    <th className="pb-3 pr-4">Score</th>
                    <th className="pb-3 pr-4">Status</th>
                    <th className="pb-3">Notes</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    { title: "Senior Product Manager", company: "TechCorp", location: "San Francisco, CA", salary: "$160-200K", score: 87, status: "Interview", notes: "2nd round scheduled" },
                    { title: "Technical PM", company: "DataFlow", location: "Remote", salary: "$150-180K", score: 82, status: "Applied", notes: "Resume submitted" },
                    { title: "Product Lead", company: "CloudScale", location: "New York, NY", salary: "$180-220K", score: 78, status: "Saved", notes: "Reach out to recruiter" },
                    { title: "Director of Product", company: "StartupX", location: "Austin, TX", salary: "$170-210K", score: 75, status: "Applied", notes: "Waiting for response" },
                  ].map((job, idx) => (
                    <tr key={idx} className="border-b border-[#bedcee]/50 hover:bg-[#c5f0f0]/10">
                      <td className="py-3 pr-4 font-medium text-[#1c3d51]">{job.title}</td>
                      <td className="py-3 pr-4 text-[#748491]">{job.company}</td>
                      <td className="py-3 pr-4 text-[#748491]">{job.location}</td>
                      <td className="py-3 pr-4 text-[#748491]">{job.salary}</td>
                      <td className="py-3 pr-4">
                        <Badge className="bg-[#2a9695] text-white">{job.score}%</Badge>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant="outline" className="border-[#2a9695] text-[#2a9695]">
                          {job.status}
                        </Badge>
                      </td>
                      <td className="py-3 text-xs text-[#748491]">{job.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-[#bedcee] pt-4">
              <p className="text-sm text-[#748491]">Showing 4 of 24 applications</p>
              <div className="flex items-center gap-2">
                <Badge className="bg-[#2a9695] text-white">12 Saved</Badge>
                <Badge className="bg-[#778ec3] text-white">8 Applied</Badge>
                <Badge className="bg-[#1F5867] text-white">4 Interview</Badge>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* AI Resume Match Section */}
      <section className="bg-gradient-to-br from-[#1c3d51] to-[#2b4276] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-2xl font-bold text-white md:text-3xl">
              Discover Which Jobs Your Resume Actually Fits
            </h2>
            <p className="text-lg text-[#bedcee]">
              AI-powered analysis shows your match percentage for every role
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {roleMatchData.map((job, idx) => (
              <Card key={idx} className="border-[#78d0d0]/30 p-6">
                <div className="mb-4">
                  <h3 className="mb-2 text-lg font-semibold text-[#1c3d51]">{job.role}</h3>
                  <div className="mb-2 flex items-center gap-2">
                    <Progress value={job.match} className="h-2 flex-1" />
                    <span className="text-2xl font-bold text-[#2a9695]">{job.match}%</span>
                  </div>
                  <p className="text-xs text-[#748491]">Match Score</p>
                </div>
                <div className="space-y-2 rounded-lg bg-[#c5f0f0]/20 p-3">
                  <p className="text-xs font-medium text-[#1c3d51]">Top Matching Skills:</p>
                  <div className="flex flex-wrap gap-1">
                    {["Product Strategy", "SQL", "Agile"].map((skill, i) => (
                      <Badge
                        key={i}
                        variant="outline"
                        className="border-[#2a9695] text-xs text-[#2a9695]"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Career Insights Dashboard Section */}
      <section className="bg-white px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-2xl font-bold text-[#1c3d51] md:text-3xl">
              Turn Your Job Search Into Data
            </h2>
            <p className="text-lg text-[#748491]">Powerful analytics to optimize your applications</p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Card className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="rounded-lg bg-[#2a9695] p-2">
                  <Target className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-[#1c3d51]">ATS Keywords</h3>
              </div>
              <p className="mb-4 text-sm text-[#748491]">
                See which keywords appear most across job descriptions you&apos;re a strong fit for.
              </p>
              <div className="space-y-2">
                {[
                  { keyword: "Product Strategy", count: 24 },
                  { keyword: "SQL", count: 21 },
                  { keyword: "Agile", count: 19 },
                  { keyword: "Python", count: 15 },
                  { keyword: "A/B Testing", count: 12 },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between rounded-lg bg-[#c5f0f0]/20 px-3 py-2"
                  >
                    <span className="text-sm text-[#1c3d51]">{item.keyword}</span>
                    <Badge className="bg-[#2a9695] text-xs text-white">{item.count}</Badge>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="rounded-lg bg-[#778ec3] p-2">
                  <Briefcase className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-[#1c3d51]">Top Role Titles</h3>
              </div>
              <p className="mb-4 text-sm text-[#748491]">
                Discover which job titles you&apos;re a strong fit for.
              </p>
              <div className="space-y-2">
                {[
                  { title: "Product Manager", count: 12 },
                  { title: "Senior PM", count: 8 },
                  { title: "Technical PM", count: 6 },
                  { title: "Product Lead", count: 4 },
                ].map((role, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-sm text-[#1c3d51]">{role.title}</span>
                    <Badge className="bg-[#c5f0f0] text-xs text-[#1c3d51]">{role.count}</Badge>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="rounded-lg bg-[#1F5867] p-2">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-[#1c3d51]">Top Skills in Best-Fit Jobs</h3>
              </div>
              <p className="mb-4 text-sm text-[#748491]">
                See which skills appear most across jobs you&apos;re a strong fit for to prioritize
                what to highlight.
              </p>
              <div className="space-y-2">
                <div className="rounded-lg bg-red-50 p-2">
                  <p className="mb-1 text-xs font-medium text-red-900">Must Have</p>
                  <div className="flex flex-wrap gap-1">
                    {["Product Strategy", "SQL", "Agile"].map((skill, i) => (
                      <Badge key={i} className="bg-red-100 text-xs text-red-800">{skill}</Badge>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg bg-yellow-50 p-2">
                  <p className="mb-1 text-xs font-medium text-yellow-900">Nice to Have</p>
                  <div className="flex flex-wrap gap-1">
                    {["Python", "A/B Testing"].map((skill, i) => (
                      <Badge key={i} className="bg-yellow-100 text-xs text-yellow-800">{skill}</Badge>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg bg-blue-50 p-2">
                  <p className="mb-1 text-xs font-medium text-blue-900">Bonus</p>
                  <div className="flex flex-wrap gap-1">
                    {["Tableau", "Figma"].map((skill, i) => (
                      <Badge key={i} className="bg-blue-100 text-xs text-blue-800">{skill}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Feature Grid Section */}
      <section className="bg-[#EFEFEF] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-2xl font-bold text-[#1c3d51] md:text-3xl">
              Everything You Need to Land Your Next Role
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Link2, title: "Paste Job Links", desc: "Add jobs instantly by pasting links—HireFit extracts all the details automatically" },
              { icon: Sparkles, title: "AI Resume Match Scores", desc: "See your match percentage for each role based on your resume" },
              { icon: FileText, title: "Multiple Resume Management", desc: "Upload different resume versions and track which one you used for each application" },
              { icon: BarChart3, title: "Skill & Salary Insights", desc: "View location, salary ranges, and skill requirements in one organized table" },
              { icon: Target, title: "Status Tracking", desc: "Update application status with dropdowns—from Saved to Interview to Offer" },
              { icon: CheckCircle2, title: "Score Filtering", desc: "Filter jobs by match score to focus on your best opportunities" },
            ].map((feature, idx) => (
              <Card
                key={idx}
                className="bg-white p-6 transition-all hover:border-[#2a9695] hover:shadow-lg"
              >
                <feature.icon className="mb-4 h-8 w-8 text-[#2a9695]" />
                <h3 className="mb-2 font-semibold text-[#1c3d51]">{feature.title}</h3>
                <p className="text-sm text-[#748491]">{feature.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Founding Members — shown when userCount <= 1000 */}
      {showFoundingMembers && (
        <section className="bg-gradient-to-br from-[#1c3d51] via-[#2b4276] to-[#1F5867] px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <Badge className="mb-6 border border-[#C6A85E] bg-[#C6A85E] text-white">
              <Sparkles className="mr-1 h-3 w-3" />
              Limited Time Offer
            </Badge>
            <h2 className="mb-4 text-2xl font-bold text-white md:text-3xl">Elite Members</h2>
            <p className="mb-8 text-lg text-[#bedcee]">
              The next 1,000 users receive permanent access to the features available at signup.
            </p>
            <Link
              href="/login"
              className="group inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#C6A85E] to-[#2a9695] px-6 py-3 text-white hover:opacity-90"
            >
              Claim Your Elite Member Spot
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </section>
      )}

      {/* Final CTA — shown when userCount > 1000 */}
      {!showFoundingMembers && (
        <section className="bg-white px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-4 text-2xl font-bold text-[#1c3d51] md:text-3xl">
              Start Tracking Your Job Applications Today
            </h2>
            <p className="mb-8 text-lg text-[#748491]">
              Join thousands of job seekers who&apos;ve streamlined their search with HireFit
            </p>
            <Link
              href="/login"
              className="group inline-flex items-center gap-2 rounded-lg bg-[#2a9695] px-6 py-3 text-white hover:bg-[#1F5867]"
            >
              Create Free Account
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </section>
      )}

      <MarketingFooter />
    </div>
  );
}
